import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Waves, Trophy, Users, HeartHandshake, Building2, Gauge, Sparkles,
  Repeat, Mail, ArrowRight, ShieldCheck, LineChart, Handshake, LogIn,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Brand } from "@/components/Brand";
import { api, money } from "@/lib/api";

const GIVEBUTTER = "https://givebutter.com/LITTLESPLASHSWIMPROGRAM";

const HERO_IMG = "/brand/IMG_9518.jpeg";
const COMMUNITY_IMG = "/brand/IMG_9508.jpeg";

const FEATURES = [
  { icon: Sparkles, title: "Why team fundraising works", body: "Shared goals build momentum individuals can't match. Friends, family and colleagues rally each other, widen the network, and give more together." },
  { icon: Waves, title: "What team fundraising is", body: "Supporters form groups around a cause, set a collective target, and each member raises toward it — one team, one goal, many hands." },
  { icon: Users, title: "Simple team creation & management", body: "Spin up a team in seconds from an invite link. Members, goals and progress stay organised without spreadsheets or chasing." },
  { icon: Trophy, title: "Friendly competition", body: "Live leaderboards, momentum badges, and 25/50/75/100% milestones keep teams motivated and racing to the top." },
  { icon: Mail, title: "Automated supporter journeys", body: "Welcome, milestone and campaign-update emails send themselves at the right moment — steady encouragement, zero manual effort." },
  { icon: Building2, title: "Corporate & workplace fundraising", body: "Sponsorship tiers, in-kind asks and business relationships tracked in one pipeline — from prospect to recognised partner." },
  { icon: Gauge, title: "Real-time team performance", body: "Every gift updates totals, progress bars and standings instantly. Staff see exactly where each team and campaign stands." },
  { icon: LineChart, title: "Scale without extra admin", body: "Ten teams or a hundred — invite links, automated emails and live totals mean growth doesn't grow your workload." },
  { icon: Repeat, title: "Turn teams into long-term supporters", body: "Celebrate wins and keep supporters connected so a single campaign becomes a lasting relationship with the mission." },
  { icon: HeartHandshake, title: "Bring the community together", body: "A shared home for the whole effort creates belonging — everyone pulling in the same direction for local kids." },
];

function useAggregate() {
  const [agg, setAgg] = useState({ raised: 0, teams: 0, supporters: 0 });
  useEffect(() => {
    api.get("/campaigns").then(({ data }) => {
      setAgg({
        raised: data.reduce((s, c) => s + (c.raised || 0), 0),
        teams: data.reduce((s, c) => s + (c.team_count || 0), 0),
        supporters: data.reduce((s, c) => s + (c.supporter_count || 0), 0),
      });
    }).catch(() => {});
  }, []);
  return agg;
}

const NAV = [
  { label: "Why teams", href: "#why" },
  { label: "How it works", href: "#features" },
  { label: "Competition", href: "#compete" },
  { label: "Sponsors", href: "#sponsors" },
];

export default function Landing() {
  const agg = useAggregate();

  return (
    <div className="min-h-screen bg-[#f8f3e9]">
      {/* Nav */}
      <header className="sticky top-0 z-50 hf-glass border-b border-[#e4d8c4]/70">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5">
          <Brand />
          <nav className="hidden items-center gap-7 md:flex">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} className="text-sm font-semibold text-[#3f6670] transition-colors hover:text-[#c97049]">
                {n.label}
              </a>
            ))}
          </nav>
          <Link to="/login" data-testid="landing-login-button">
            <Button className="rounded-full bg-[#00334a] hover:bg-[#00334a] font-semibold">
              <LogIn className="mr-2 h-4 w-4" /> Staff / Supporter Login
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden hf-caustics">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 py-16 lg:grid-cols-2 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="hf-eyebrow">Confidence Starts Here · Team Fundraising</span>
            <h1 className="mt-4 font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-[#00334a] sm:text-5xl lg:text-6xl">
              Turn collective waves into{" "}
              <span className="text-[#c97049]">swimming scholarships</span>.
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-[#3f6670] sm:text-lg">
              Hope Floats is the private fundraising home for Little Splash Swim Program staff and
              supporters. Form teams, chase shared goals, climb the leaderboard, and make sure cost
              is never the reason a child can't learn to swim.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a href={GIVEBUTTER} target="_blank" rel="noopener noreferrer" data-testid="hero-donate-button">
                <Button className="hf-btn-coral rounded-full px-7 py-6 text-base font-bold">
                  Donate now <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
              <Link to="/login" data-testid="hero-login-button">
                <Button variant="outline" className="rounded-full border-[#65949c] px-7 py-6 text-base font-bold text-[#65949c] hover:bg-[#efe6d6]">
                  Start or join a team
                </Button>
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              {[
                { k: money(agg.raised), v: "Raised together" },
                { k: agg.teams, v: "Active teams" },
                { k: agg.supporters, v: "Supporters" },
              ].map((s, i) => (
                <div key={s.v} className="rounded-2xl border border-[#e4d8c4] bg-white/80 p-3 text-center" data-testid={`hero-stat-${i}`}>
                  <div className="font-heading text-2xl font-extrabold text-[#65949c]">{s.k}</div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-[#7c7a6a]">{s.v}</div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="relative"
          >
            <div className="hf-float overflow-hidden rounded-[2rem] border-4 border-white shadow-2xl">
              <img src={HERO_IMG} alt="Child learning to swim" className="h-[420px] w-full object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-4 rounded-2xl border border-[#e4d8c4] bg-white p-4 shadow-xl sm:-left-6">
              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#f3e7db]">
                  <ShieldCheck className="h-5 w-5 text-[#c97049]" />
                </div>
                <div>
                  <div className="font-heading text-sm font-bold text-[#00334a]">Water safety saves lives</div>
                  <div className="text-xs text-[#7c7a6a]">Drowning is the #1 accidental death for ages 1–4</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Challenge vs solution */}
      <section id="why" className="mx-auto max-w-7xl px-5 py-16">
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-3xl border border-[#e4d8c4] bg-white p-8">
            <span className="hf-eyebrow">The challenge</span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-[#00334a]">Coordinating teams gets complex fast</h2>
            <ul className="mt-4 space-y-3 text-[#5a5849]">
              {["Scattered spreadsheets and group chats", "No live view of who raised what", "Manual, easily-forgotten follow-ups", "Hard to scale past a handful of teams"].map((t) => (
                <li key={t} className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#a9a795]" />{t}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl hf-gradient-deep p-8 text-white">
            <span className="text-[11px] font-bold uppercase tracking-widest text-sky-200">How Hope Floats solves it</span>
            <h2 className="mt-2 font-heading text-2xl font-bold">One calm, real-time command center</h2>
            <ul className="mt-4 space-y-3 text-sky-50">
              {["Invite links create teams & accounts instantly", "Leaderboards & totals update on every gift", "Milestone & welcome emails send automatically", "Grows to any size with zero extra admin"].map((t) => (
                <li key={t} className="flex gap-3"><Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[#c99a4e]" />{t}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section id="features" className="mx-auto max-w-7xl px-5 py-8">
        <div className="mx-auto max-w-2xl text-center">
          <span className="hf-eyebrow">Everything a team needs</span>
          <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight text-[#00334a] sm:text-4xl">
            Built for collective giving
          </h2>
        </div>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
              className="group rounded-2xl border border-[#e4d8c4] bg-white p-6 transition-shadow hover:shadow-lg"
              id={i === 3 ? "compete" : undefined}
            >
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-[#efe6d6] text-[#65949c] transition-colors group-hover:bg-[#c97049] group-hover:text-white">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-heading text-lg font-bold text-[#00334a]">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#7c7a6a]">{f.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Sponsors */}
      <section id="sponsors" className="mx-auto max-w-7xl px-5 py-16">
        <div className="overflow-hidden rounded-3xl border border-[#e4d8c4] bg-white">
          <div className="grid lg:grid-cols-2">
            <img src={COMMUNITY_IMG} alt="Community celebrating together" className="h-full min-h-[280px] w-full object-cover" />
            <div className="p-8 lg:p-10">
              <span className="hf-eyebrow">Corporate & workplace</span>
              <h2 className="mt-2 font-heading text-3xl font-bold text-[#00334a]">Sponsor a swimmer, sponsor a splash</h2>
              <p className="mt-3 text-[#7c7a6a]">
                Businesses can back a child's lessons directly or sponsor our fundraising events.
                Staff track every relationship — from first conversation to public thank-you.
              </p>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {[
                  { t: "Swimmer Sponsorship", d: "$300–$400 · one scholarship" },
                  { t: "Guppy", d: "$100 event tier" },
                  { t: "Dolphin", d: "$250 event tier" },
                  { t: "Wave Maker", d: "$500 · founding sponsor" },
                ].map((s) => (
                  <div key={s.t} className="rounded-xl bg-[#f8f3e9] p-3">
                    <div className="flex items-center gap-2 font-heading font-bold text-[#00334a]">
                      <Handshake className="h-4 w-4 text-[#c97049]" /> {s.t}
                    </div>
                    <div className="text-xs text-[#7c7a6a]">{s.d}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="hf-gradient-deep text-white">
        <div className="mx-auto max-w-7xl px-5 py-12">
          <div className="flex flex-col gap-8 md:flex-row md:justify-between">
            <div className="max-w-sm">
              <Brand light />
              <p className="mt-4 text-sm text-sky-100">
                A private fundraising portal for the Little Splash Swim Program. This site is not
                linked from our public homepage — access is by invitation.
              </p>
            </div>
            <div className="text-sm text-sky-100">
              <div className="font-heading font-bold text-white">Hope Floats Foundation</div>
              <p className="mt-2">501(c)(3) · checks payable to<br/>"Hope Floats Foundation / Little Splash Swim Program"</p>
              <p className="mt-1">PO Box 23, Simsbury, CT 06070</p>
              <a href="mailto:hello@littlesplashswimpro.com" className="mt-2 inline-block underline decoration-sky-300 hover:text-white">
                hello@littlesplashswimpro.com
              </a>
            </div>
            <div>
              <a href={GIVEBUTTER} target="_blank" rel="noopener noreferrer">
                <Button className="hf-btn-coral rounded-full font-bold">Give on Givebutter</Button>
              </a>
            </div>
          </div>
          <div className="mt-10 border-t border-white/15 pt-6 text-xs text-sky-200">
            © {new Date().getFullYear()} LSSP × Hope Floats Foundation. All gifts are tax-deductible to the extent allowed by law.
          </div>
        </div>
      </footer>
    </div>
  );
}
